import React from "react";
import { Button as MUIButton, ButtonProps, SxProps } from "@mui/material";
import { useTheme } from "contexts";
import { Validations } from "helpers";

// icons
import {
  AddRounded,
  ArrowOutwardRounded,
  ChevronRight,
  DeleteRounded,
  DriveFileRenameOutlineOutlined,
} from "@mui/icons-material";

type Props = Omit<ButtonProps, "type" | "color" | "sx"> & {
  submit?: boolean;
  secondary?: boolean;
  color?: string; // Custom color prop
  bgColor?: string; // Custom background color prop
  children: React.ReactNode;
  trailing?: string;
  sx?: SxProps; // Allow additional styling overrides
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
};

export const Button = React.forwardRef<HTMLButtonElement, Props>(
  (
    {
      sx = {},
      submit = false,
      secondary = false,
      color,
      bgColor,
      children,
      trailing,
      onClick,
      ...props
    },
    ref
  ) => {
    const { theme } = useTheme();

    const isOutlined = props.variant === "outlined";
    const isContained = props.variant === "contained";

    const endIcon =
      props.href && Validations.isUrl(props.href) ? (
        <ArrowOutwardRounded />
      ) : trailing === "right" ? (
        <ChevronRight />
      ) : trailing === "add" ? (
        <AddRounded />
      ) : trailing === "delete" ? (
        <DeleteRounded />
      ) : trailing === "edit" ? (
        <DriveFileRenameOutlineOutlined />
      ) : (
        props.endIcon
      );

    function handleClick(event: React.MouseEvent<HTMLButtonElement>) {
      if (onClick) onClick(event);
    }

    const currentColor = secondary
      ? color || theme.color.accent.color
      : isContained
      ? color || theme.color.accent.text
      : isOutlined
      ? color || theme.color.font.color
      : color || theme.color.accent.color;

    const currentBackgroundColor = secondary
      ? bgColor || theme.color.background.accent
      : isContained
      ? bgColor || theme.color.accent.color
      : isOutlined
      ? bgColor || theme.color.background.color
      : undefined;

    const currentBorderColor = isOutlined
      ? color || theme.color.font.color
      : undefined;

    const currentHeight = props.size
      ? undefined
      : theme.component.layout.button.height;

    return (
      <MUIButton
        ref={ref}
        sx={{
          boxShadow: "none",
          textTransform: "none",
          height: currentHeight,
          borderRadius: theme.border.radius,
          color: currentColor,
          backgroundColor: currentBackgroundColor,
          borderColor: currentBorderColor,
          fontSize: theme.font.xs,
          fontWeight: isOutlined ? theme.font.semiBold : undefined,
          "&:active": {
            boxShadow: "none",
          },
          "&:hover": {
            boxShadow: "none",
            color: currentColor,
            borderColor: currentBorderColor,
            backgroundColor: currentBackgroundColor,
          },
          ...sx,
        }}
        endIcon={endIcon}
        onClick={handleClick}
        type={submit ? "submit" : "button"}
        {...props}
      >
        {children}
      </MUIButton>
    );
  }
);
